<template>
  <div ref="root" class="carousel" @keydown.left.prevent="prev" @keydown.right.prevent="next">
    <div class="carousel-body">
      <button
        v-if="showNavigation && maxIndex > 0"
        type="button"
        class="carousel-nav carousel-nav--prev"
        :disabled="currentIndex === 0"
        :aria-label="previousLabel"
        @click="onNavClick($event, prev)"
      >
        <VSvgIcon class="carousel-nav-icon carousel-nav-icon--prev" name="navigation-down" size="small" pointer-events="none" />
      </button>

      <div
        class="carousel-viewport"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <ul
          class="carousel-track"
          :class="{ 'is-dragging': isDragging }"
          :style="trackStyle"
          role="list"
        >
          <slot></slot>
        </ul>
      </div>

      <button
        v-if="showNavigation && maxIndex > 0"
        type="button"
        class="carousel-nav carousel-nav--next"
        :disabled="currentIndex >= maxIndex"
        :aria-label="nextLabel"
        @click="onNavClick($event, next)"
      >
        <VSvgIcon class="carousel-nav-icon carousel-nav-icon--next" name="navigation-down" size="small" pointer-events="none" />
      </button>
    </div>

    <ul v-if="paginationEnabled && slideCount > 1" class="carousel-pagination" role="tablist">
      <li v-for="slide in slideCount" :key="slide" class="carousel-pagination-item">
        <button
          type="button"
          class="carousel-dot"
          :class="{ 'is-active': isVisible(slide - 1) }"
          :style="dotStyle(slide - 1)"
          :aria-label="$tc('common.resultPage', slide)"
          :aria-selected="String(isVisible(slide - 1))"
          @click="goTo(slide - 1)"
        ></button>
      </li>
    </ul>
  </div>
</template>

<script>
import VSvgIcon from '@/components/UI/VSvgIcon.vue';

/**
 * Lightweight, dependency-free carousel that is a drop-in replacement for the
 * `vue-carousel` <Carousel>/<Slide> pair used across the app.
 *
 * Unlike `vue-carousel`, this component measures its own width asynchronously
 * via ResizeObserver (with a window-resize fallback). It therefore never runs
 * the synchronous, width===0 slide-measurement loop that froze the app on
 * Android/WebKit (see contract-selection freeze).
 *
 * Supported props mirror the previously used vue-carousel API:
 *  - perPageCustom: Array<[minWidth, perPage]>
 *  - perPage: Number (fallback when perPageCustom is not provided)
 *  - paginationColor / paginationActiveColor: dot colors
 */
export default {
  name: 'Carousel',

  components: {
    VSvgIcon,
  },

  props: {
    perPageCustom: {
      type: Array,
      default: null,
    },

    perPage: {
      type: Number,
      default: 1,
    },

    paginationEnabled: {
      type: Boolean,
      default: true,
    },

    showNavigation: {
      type: Boolean,
      default: true,
    },

    paginationColor: {
      type: String,
      default: '#ededed',
    },

    paginationActiveColor: {
      type: String,
      default: '#e20074',
    },

    previousLabel: {
      type: String,
      default: 'Previous',
    },

    nextLabel: {
      type: String,
      default: 'Next',
    },
  },

  data() {
    return {
      containerWidth: 0,
      currentIndex: 0,
      slideCount: 0,
      isDragging: false,
      touchStartX: 0,
      touchDeltaX: 0,
    };
  },

  computed: {
    computedPerPage() {
      if (!this.perPageCustom || !this.perPageCustom.length) {
        return Math.max(1, this.perPage);
      }
      // Pick the perPage of the largest breakpoint whose minWidth <= containerWidth.
      const sorted = [...this.perPageCustom].sort((a, b) => a[0] - b[0]);
      let result = sorted[0][1];
      sorted.forEach(([minWidth, count]) => {
        if (this.containerWidth >= minWidth) {
          result = count;
        }
      });
      return Math.max(1, result);
    },

    // Highest index we may scroll to so the last slide still aligns to the right
    // edge (no empty trailing space).
    maxIndex() {
      return Math.max(0, this.slideCount - this.computedPerPage);
    },

    trackStyle() {
      // Navigation steps one slide at a time; each slide is (100% / perPage) wide,
      // so the track shifts by that fraction per index. The `--carousel-per-page`
      // custom property is inherited by the child <Slide> elements to size them.
      const step = 100 / this.computedPerPage;
      const dragOffset = this.containerWidth ? (this.touchDeltaX / this.containerWidth) * 100 : 0;
      return {
        transform: `translateX(calc(-${this.currentIndex * step}% + ${dragOffset}%))`,
        '--carousel-per-page': this.computedPerPage,
      };
    },
  },

  watch: {
    computedPerPage() {
      this.clampIndex();
    },
  },

  mounted() {
    this.countSlides();
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.$refs.root);
    } else {
      this.onResize = () => this.measure();
      window.addEventListener('resize', this.onResize);
    }
  },

  updated() {
    this.countSlides();
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }
  },

  methods: {
    countSlides() {
      const slides = (this.$slots.default || []).filter((vnode) => vnode.tag);
      if (slides.length !== this.slideCount) {
        this.slideCount = slides.length;
        this.clampIndex();
      }
    },

    // Layout -----------------------------------------------------------------
    measure() {
      if (!this.$refs.root) return;
      const width = this.$refs.root.offsetWidth;
      // Guard: only react to real widths; a 0 width simply keeps the previous
      // value and we re-measure when the next resize event arrives. This is the
      // key difference from vue-carousel, which looped synchronously on width 0.
      if (width > 0 && width !== this.containerWidth) {
        this.containerWidth = width;
        this.clampIndex();
      }
    },

    clampIndex() {
      if (this.currentIndex > this.maxIndex) {
        this.currentIndex = this.maxIndex;
      }
    },

    // Navigation -------------------------------------------------------------
    prev() {
      if (this.currentIndex > 0) this.currentIndex -= 1;
    },

    next() {
      if (this.currentIndex < this.maxIndex) this.currentIndex += 1;
    },

    // Run a nav action then drop focus from the button, so the magenta
    // hover/focus color never persists on the last-clicked arrow (this was the
    // inconsistency where one arrow stayed magenta, especially on touch where
    // there is no hover to clear it). Keyboard users keep the focus ring because
    // key activation re-establishes :focus-visible.
    onNavClick(event, action) {
      action();
      if (event.currentTarget && typeof event.currentTarget.blur === 'function') {
        event.currentTarget.blur();
      }
    },

    goTo(index) {
      this.currentIndex = Math.min(Math.max(0, index), this.maxIndex);
    },

    // A slide is "visible" when it sits inside the current viewport window
    // [currentIndex, currentIndex + perPage). Used to light up its dot, so the
    // last slide's dot is reachable even though currentIndex is clamped to maxIndex.
    isVisible(index) {
      return index >= this.currentIndex && index < this.currentIndex + this.computedPerPage;
    },

    // Touch / swipe ----------------------------------------------------------
    onTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
      this.touchDeltaX = 0;
      this.isDragging = true;
    },

    onTouchMove(event) {
      if (!this.isDragging) return;
      this.touchDeltaX = event.touches[0].clientX - this.touchStartX;
    },

    onTouchEnd() {
      if (!this.isDragging) return;
      // A swipe of more than ~1/4 of a single slide's width flips one slide.
      const slideWidth = this.containerWidth / this.computedPerPage;
      const threshold = slideWidth / 4;
      if (this.touchDeltaX <= -threshold) {
        this.next();
      } else if (this.touchDeltaX >= threshold) {
        this.prev();
      }
      this.touchDeltaX = 0;
      this.isDragging = false;
    },

    dotStyle(index) {
      return {
        backgroundColor: this.isVisible(index) ? this.paginationActiveColor : this.paginationColor,
      };
    },
  },
};
</script>

<style scoped lang="scss">
.carousel {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.carousel-body {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
}

.carousel-viewport {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  touch-action: pan-y; /* allow vertical page scroll, we handle horizontal swipe */
}

.carousel-track {
  display: flex;
  padding: 0;
  margin: 0;
  list-style: none;
  transition: transform 0.3s ease;
  will-change: transform;

  &.is-dragging {
    transition: none; /* follow the finger 1:1 while dragging */
  }
}

.carousel-nav {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  margin: 0;
  color: #6a6a6a; /* neutral idle */
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: 50%;
  transition: color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  &:hover,
  &:focus-visible {
    color: #262626; /* darken on hover/focus (neutral, no brand color) */
    background-color: rgba(0, 0, 0, 0.06);
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px #b2b2b2;
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.12);
  }

  &:disabled {
    color: #d0d0d0;
    cursor: default;
    background-color: transparent;
    box-shadow: none;
  }
}

.carousel-nav-icon {
  width: 18px;
  height: 18px;
  pointer-events: none; /* the whole button is the hover/click target, not the svg */

  &--prev {
    transform: rotate(90deg); /* down chevron -> points left */
  }

  &--next {
    transform: rotate(-90deg); /* down chevron -> points right */
  }
}

@media (max-width: 480px) {
  .carousel-nav {
    width: 32px;
    height: 32px;
  }

  .carousel-nav-icon {
    width: 16px;
    height: 16px;
  }
}

.carousel-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 12px 0 0;
  margin: 0;
  list-style: none;
}

.carousel-pagination-item {
  margin: 0 4px;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  padding: 0;
  cursor: pointer;
  border: none;
  border-radius: 50%;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.2);
  }

  &.is-active {
    transform: scale(1.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px #b2b2b2;
  }
}
</style>
